import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUIStore } from '../store/uiStore';
import { useGuideStore } from '../store/guideStore';
import { useUserStore } from '../store/userStore';
import { Save } from 'lucide-react';
import GuideSettings from '../components/features/guide/GuideSettings';
import WidgetConfig from '../components/features/guide/WidgetConfig';
import PortalSettings from '../components/features/portal/PortalSettings';
import UpsellModal from '../components/modals/UpsellModal';
import { AppLayout } from '../components/layout';
import { useClientAppSupportsCloudV2 } from '../store/userStore';
import { usePenpal } from '../hooks/usePenpal';
import * as Sentry from '@sentry/react';
import AnnouncementBanner from '../components/AnnouncementBanner';

const EditGuidePage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { currentEditTab, setCurrentEditTab, isSaving, openModal, closeModal, modals } = useUIStore();
  const { currentGuide, getGuide } = useGuideStore();
  const { properties } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isInIframe, isConnected } = usePenpal();

  const isProCloudPlan = properties?.portal?.billing?.isProPlan ?? false;
  // Show sidebar when in browser (not in iframe) OR when app supports CloudV2
  const isInBrowser = !isConnected && !isInIframe;
  const clientAppSupportsCloudV2 = useClientAppSupportsCloudV2();
  const showSidebar = isInBrowser || clientAppSupportsCloudV2;
  // Portal settings tab should only be shown when opened within the app AND app doesn't support CloudV2 (older version)
  const showPortalSettings = !isInBrowser && !clientAppSupportsCloudV2;
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch guide if it doesn't exist and we have an ID
  useEffect(() => {
    if (id) {
      const fetchGuide = async () => {
        setLoading(true);
        setError(null);
        try {
          const guide = await getGuide(id);
          if (!guide) {
            setError(t('editGuide.notFound'));
          }
        } catch (err) {
          console.error('Error fetching guide:', err);
          Sentry.captureException(err);
          setError(t('editGuide.loadFailed'));
        } finally {
          setLoading(false);
        }
      };
      fetchGuide();
    }
  }, [id, getGuide]);

  const handleTabClick = (tab: 'settings' | 'widget' | 'portal-settings') => {
    setCurrentEditTab(tab);
  };

  const handlePortalSettingsClick = (event: React.MouseEvent) => {
    if (!isProCloudPlan) {
      openModal('upsell');
      event.preventDefault();
      return;
    }
  };
  // Show loading state
  if (loading) {
    return (
      <AppLayout showSidebar={showSidebar}>
        <div className="bg-gray-50 mb-5 px-4 py-8 relative min-h-[550px] flex items-center justify-center">
          <div className="text-center">
            <Save className="animate-spin h-8 w-8 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600">{t('editGuide.loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <AppLayout showSidebar={showSidebar}>
        <div className="bg-gray-50 mb-5 px-4 py-8 relative min-h-[550px] flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            {/* <button 
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Go Back
            </button> */}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Show error if no guide and no ID
  if (!currentGuide) {
    return (
      <AppLayout showSidebar={showSidebar}>
        <div className="bg-gray-50 mb-5 px-4 py-8 relative min-h-[550px] flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{t('editGuide.notFound')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const breadcrumbs = showSidebar ? [
    { title: t('sidebar.guides'), href: 'guides' },
    { title: currentGuide.title, isCurrentPage: true }
  ] : [];

  return (
    <AppLayout showSidebar={showSidebar} breadcrumbs={breadcrumbs} defaultSidebarOpen={false}>
      <div className="bg-gray-50 mb-5 px-4 py-8 relative min-h-[550px]">
      {/* Announcement Banner */}
      <AnnouncementBanner />
      {/* Back Button */}
      {/* <div className="mb-6">
        <button 
          onClick={goBack}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </button>
      </div> */}

      {/* Saving Indicator */}
      {isSaving && (
        <div className="absolute top-[30px] left-[50%] transform -translate-x-1/2 z-10 bg-white rounded-full p-5 border shadow">
          <Save className="animate-spin h-6 w-6 text-gray-800" />
        </div>
      )}

      {/* Tab Navigation */}
      <nav className="flex gap-3 w-[750px] mx-auto" aria-label={t('editGuide.tabsAriaLabel')}>
        <button
          onClick={() => handleTabClick('settings')}
          className={`shrink-0 p-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-b-0 ${
            currentEditTab === 'settings'
              ? 'hover:bg-gray-100 bg-white'
              : 'hover:bg-white bg-gray-100'
          }`}
        >
          {t('editGuide.tabSettings')}
        </button>

        <button
          onClick={() => handleTabClick('widget')}
          className={`shrink-0 p-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-b-0 ${
            currentEditTab === 'widget'
              ? 'hover:bg-gray-100 bg-white'
              : 'hover:bg-white bg-gray-100'
          }`}
        >
          {t('editGuide.tabWidget')}
        </button>

        {showPortalSettings && <button
          onClick={() => handleTabClick('portal-settings')}
          className={`shrink-0 p-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-b-0 ${
            currentEditTab === 'portal-settings'
              ? 'hover:bg-gray-100 bg-white'
              : 'hover:bg-white bg-gray-100'
          }`}
        >
          {t('editGuide.tabBranding')}
        </button>}
      </nav>

      {/* Tab Content */}
      <div className="">
        {currentEditTab === 'settings' && <GuideSettings />}
        {currentEditTab === 'widget' && <WidgetConfig />}
        {currentEditTab === 'portal-settings' && (
          <div onClick={handlePortalSettingsClick}>
            <PortalSettings 
              settings={properties?.portal?.settings} 
              isProCloudPlan={isProCloudPlan}
              context="guide"
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <UpsellModal 
        isOpen={modals.upsell} 
        onClose={() => closeModal('upsell')} 
      />
      </div>
    </AppLayout>
  );
};

export default EditGuidePage; 